/**
 * maxMoves — Max Moves (Dynamax / Gigantamax) as Pokémon GO models them.
 *
 * Every Max-capable Pokémon has three slots, in GO's own order:
 *
 *   ATTACK   the generic Max Move of its fast move's type (Max Flare for a
 *            Fire fast move), the species' own G-Max move for a Gigantamax,
 *            or a signature move for the three that use Max Moves without
 *            Dynamaxing (Crowned Zacian and Zamazenta, Eternatus). Starts
 *            unlocked at level 1.
 *   GUARD    Max Guard: temporary HP shields. Starts LOCKED.
 *   SPIRIT   Max Spirit: heals the party. Starts LOCKED.
 *
 * Each levels 1 to 3; GO prints level 3 as "MAX!". The Game Master carries
 * the moves, the type and species mappings and the unlock costs (see
 * generated/maxMoves.generated.ts), but no powers or effects: those are the
 * community-verified figures below (Bulbapedia, Max Move, 2026-09-04).
 *
 * Pure module: no DOM, no store. The species page, the record page and the
 * scanner's name matcher all read it.
 */
import {
  GMAX_MOVE_MAPPINGS,
  MAX_GUARD_ID,
  MAX_MOVES,
  MAX_MOVE_BY_TYPE,
  MAX_MOVE_COSTS,
  MAX_SPIRIT_ID,
  SPECIES_MAX_TIER,
  type GeneratedMaxMove,
  type MaxMoveKind,
  type MaxMoveLevelCost,
} from "./generated/maxMoves.generated";
import { getSpeciesMovePool } from "./movePool";
import { variantSupportsSpecies } from "./speciesVariants";
import { isPokeType, type PokeType } from "./types";

export type { GeneratedMaxMove, MaxMoveKind, MaxMoveLevelCost };

export type MaxMoveSlot = "attack" | "guard" | "spirit";
export const MAX_MOVE_SLOTS: readonly MaxMoveSlot[] = ["attack", "guard", "spirit"];

/** Max Move levels. GO shows 3 as "MAX!". 0 is a slot still locked. */
export type MaxMoveLevel = 1 | 2 | 3;
export type MaxMoveSlotLevel = 0 | MaxMoveLevel;
export const MAX_MOVE_MAX_LEVEL: MaxMoveLevel = 3;

/** Base power per level (index 0 = level 1). Behemoth Blade/Bash use the Max row. */
export const MAX_ATTACK_POWER: readonly number[] = [250, 300, 350];
/** G-Max moves, and Dynamax Cannon. A level-1 G-Max equals a level-3 Max. */
export const GMAX_ATTACK_POWER: readonly number[] = [350, 400, 450];
/** Max Guard: HP per shield, per level. */
export const MAX_GUARD_SHIELD_HP: readonly number[] = [20, 40, 60];
/** Max Spirit: percent of the user's max HP healed, per level. */
export const MAX_SPIRIT_HEAL_PCT: readonly number[] = [8, 12, 16];

/**
 * Species that use Max Moves in Max Battles although they cannot Dynamax
 * (so they are not in the Dynamax dex): Crowned Sword Zacian, Crowned
 * Shield Zamazenta, Eternatus. Their attack slot is a signature move and
 * their Guard/Spirit are the plainly-named copies GO prints.
 */
export const MAX_BATTLE_ONLY_SPECIES: readonly number[] = [888, 889, 890];

const MOVE_BY_ID = new Map<string, GeneratedMaxMove>(MAX_MOVES.map((m) => [m.id, m]));

/** "G-Max Wild fire" / "MAX GUARD" / "Behemoth  Bash" all compare equal. */
export function maxMoveNameKey(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "");
}

const MOVE_BY_NAME_KEY = new Map<string, GeneratedMaxMove>();
for (const m of MAX_MOVES) {
  if (!MOVE_BY_NAME_KEY.has(maxMoveNameKey(m.name))) MOVE_BY_NAME_KEY.set(maxMoveNameKey(m.name), m);
}

export function maxMoveById(id: string): GeneratedMaxMove | null {
  return MOVE_BY_ID.get(id) ?? null;
}

/**
 * The Max Move a printed name refers to, or null. GO labels the locked
 * Guard/Spirit tiles "Guard" and "Spirit" (and always does on Zacian,
 * Zamazenta and Eternatus), so those resolve to the canonical pair too.
 */
export function matchMaxMoveName(raw: string | null | undefined): GeneratedMaxMove | null {
  if (!raw) return null;
  const key = maxMoveNameKey(raw);
  if (key.length < 4) return null;
  if (key === "guard" || key === "maxguard") return MOVE_BY_ID.get(MAX_GUARD_ID) ?? null;
  if (key === "spirit" || key === "maxspirit") return MOVE_BY_ID.get(MAX_SPIRIT_ID) ?? null;
  const exact = MOVE_BY_NAME_KEY.get(key);
  if (exact) return exact;
  // OCR drops the hyphen glyph in "G-Max" more often than not.
  if (key.startsWith("gmax") || key.startsWith("max")) return null;
  const withMax = MOVE_BY_NAME_KEY.get(`max${key}`);
  if (withMax && withMax.kind === "max") return withMax;
  return null;
}

/** Which slot a move belongs in. */
export function maxMoveSlotOf(move: GeneratedMaxMove): MaxMoveSlot {
  return move.kind === "guard" ? "guard" : move.kind === "spirit" ? "spirit" : "attack";
}

/** Whether the attack move uses the G-Max power row. */
export function usesGmaxPower(move: GeneratedMaxMove): boolean {
  return move.kind === "gmax" || move.name === "Dynamax Cannon";
}

/** Base power of an attacking Max Move at a level, or null for Guard/Spirit. */
export function maxMovePower(move: GeneratedMaxMove, level: MaxMoveLevel): number | null {
  if (move.kind === "guard" || move.kind === "spirit") return null;
  return (usesGmaxPower(move) ? GMAX_ATTACK_POWER : MAX_ATTACK_POWER)[level - 1] ?? null;
}

/** The generic attacking Max Move for a fast-move type. Hidden Power is Normal. */
export function maxMoveForType(type: string): GeneratedMaxMove | null {
  const id = MAX_MOVE_BY_TYPE[type.toLowerCase()];
  return id ? (MOVE_BY_ID.get(id) ?? null) : null;
}

/** The type a Max Move draws with (Guard/Spirit are typeless in GO's art). */
export function maxMoveType(move: GeneratedMaxMove): PokeType | null {
  if (move.kind === "guard" || move.kind === "spirit") return null;
  return isPokeType(move.type) ? move.type : null;
}

/**
 * Whether a species has Max Moves at all: the Dynamax roster (which G-Max
 * is a subset of), plus the three Max-Battle-only legendaries.
 */
export function speciesUsesMaxMoves(dexId: number): boolean {
  return MAX_BATTLE_ONLY_SPECIES.includes(dexId) || variantSupportsSpecies("dynamax", dexId);
}

/** One attack-slot option: the Max Move and the fast moves that grant it. */
export interface MaxAttackOption {
  move: GeneratedMaxMove;
  /** Fast move names of the species that produce this Max Move ("Fire Spin"). Empty when fixed. */
  fastMoves: string[];
}

export interface SpeciesMaxMoveSet {
  /** Attack-slot options, first is the default to show. One entry when fixed. */
  attack: MaxAttackOption[];
  /** True for a G-Max or signature attack: it cannot be changed by a Fast TM. */
  fixed: boolean;
  guard: GeneratedMaxMove;
  spirit: GeneratedMaxMove;
}

/**
 * The Max Moves a species can have.
 *
 *   - Crowned Zacian / Zamazenta and Eternatus: their signature attack, and
 *     the "Guard" / "Spirit" copies.
 *   - `gmax`: the species' G-Max move (the Gigantamax form's fixed attack).
 *   - otherwise the generic Max Move per fast-move type in the species'
 *     Game Master pool, so a Charizard lists Max Flare, Max Airstream and
 *     Max Wyrmwind with the fast moves that give each.
 *
 * Null when the species has no Max Moves (or no move pool to derive from).
 */
export function speciesMaxMoves(
  dexId: number,
  opts: { gmax?: boolean; formId?: string | null; formLabel?: string | null } = {},
): SpeciesMaxMoveSet | null {
  const guard = MOVE_BY_ID.get(MAX_GUARD_ID);
  const spirit = MOVE_BY_ID.get(MAX_SPIRIT_ID);
  if (!guard || !spirit) return null;

  const rows = GMAX_MOVE_MAPPINGS.filter((r) => r.dexId === dexId);
  const signature = rows.find((r) => r.guard != null || r.spirit != null);
  if (signature) {
    const wantForm = opts.formLabel ? maxMoveNameKey(opts.formLabel) : null;
    const row =
      rows.find((r) => r.form != null && wantForm != null && maxMoveNameKey(r.form) === wantForm) ?? signature;
    const attack = MOVE_BY_ID.get(row.move);
    if (!attack) return null;
    return {
      attack: [{ move: attack, fastMoves: [] }],
      fixed: true,
      guard: (row.guard && MOVE_BY_ID.get(row.guard)) || guard,
      spirit: (row.spirit && MOVE_BY_ID.get(row.spirit)) || spirit,
    };
  }

  if (opts.gmax) {
    const wantForm = opts.formLabel ? maxMoveNameKey(opts.formLabel) : null;
    const row =
      rows.find((r) => r.form != null && wantForm != null && maxMoveNameKey(r.form) === wantForm) ??
      rows.find((r) => r.form == null) ??
      rows[0];
    const attack = row ? MOVE_BY_ID.get(row.move) : undefined;
    if (attack) return { attack: [{ move: attack, fastMoves: [] }], fixed: true, guard, spirit };
  }

  if (!speciesUsesMaxMoves(dexId)) return null;
  const pool = getSpeciesMovePool(dexId, opts.formId ?? null);
  const byMove = new Map<string, MaxAttackOption>();
  for (const fast of pool.fast) {
    // Hidden Power's Max Move is always Max Strike, whatever the roll.
    const type = /hidden\s*power/i.test(fast.name) ? "normal" : fast.type;
    const move = maxMoveForType(type);
    if (!move) continue;
    const opt = byMove.get(move.id) ?? { move, fastMoves: [] };
    if (!opt.fastMoves.includes(fast.name)) opt.fastMoves.push(fast.name);
    byMove.set(move.id, opt);
  }
  if (byMove.size === 0) return null;
  return { attack: [...byMove.values()], fixed: false, guard, spirit };
}

/**
 * What each level of a slot costs the species (index 0 = reaching level 1).
 * The attack slot's level 1 is free (it comes unlocked), so its first entry
 * is null. Null altogether when the species has no cost tier.
 */
export function maxMoveCosts(dexId: number, slot: MaxMoveSlot): Array<MaxMoveLevelCost | null> | null {
  const tier = SPECIES_MAX_TIER[dexId];
  const table = tier ? MAX_MOVE_COSTS[tier] : undefined;
  if (!table) return null;
  const rows = slot === "attack" ? table.a : slot === "guard" ? table.b : table.c;
  return rows.map((c, i) => (slot === "attack" && i === 0 ? null : c));
}

/**
 * Parse the label GO prints on a Max Move tile: "MAX!" (level 3), "LOCKED"
 * (0), or a level ("LV. 2"). Null for anything else, so page chrome can
 * never read as a level.
 */
export function parseMaxMoveTileLabel(raw: string | null | undefined): MaxMoveSlotLevel | null {
  if (!raw) return null;
  const t = raw.trim().toUpperCase().replace(/\s+/g, " ");
  if (/^MAX[!I1|]?$/.test(t)) return 3;
  if (/^LOCKED$/.test(t)) return 0;
  const lv = /^(?:LV\.?|LEVEL)\s*([123])$/.exec(t);
  if (lv) return Number(lv[1]) as MaxMoveLevel;
  return null;
}

/** "MAX!" / "Locked" / "Lv. 2", the way GO prints a slot's state. */
/**
 * The Max Attack level a surface draws as pips (2026-09-29): the level read
 * off the panel or set on the record page, else 1. There is no level zero:
 * a Dynamax Pokémon learns its Max Attack at level 1 and can buy two
 * upgrades, so an unread copy shows one filled pip, never an empty row
 * (owner direction, same day).
 */
export function readMaxAttackLevel(r: { maxMoves?: { attack?: { level: MaxMoveLevel } } | null }): MaxMoveLevel {
  const l = r.maxMoves?.attack?.level;
  return l === 2 || l === 3 ? l : 1;
}

export function maxMoveLevelLabel(level: MaxMoveSlotLevel | null | undefined): string {
  if (level == null) return "Unknown";
  if (level === 0) return "Locked";
  if (level >= MAX_MOVE_MAX_LEVEL) return "MAX!";
  return `Lv. ${level}`;
}
